import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Icon, CatBadge } from './icons';
import { AppLogo } from './AppLogo';
import { ChipFilter, TxList } from './shared';
import { relTime, money } from './format';
import { TxDetail, CategoryManager } from './sheets';
import { FocusSettings } from './settings';
import { TodayView } from './today';
import { GoalsView } from './goals';
import { BudgetView } from './budget';
import { RecurringView } from './recurring';
import { spendDelta } from '../lib/txType';
import { CashFlowView } from './cashflow';
import { AccountsView } from './accounts';
import { MonthPicker } from './MonthPicker';
import { disconnectReasonText } from '../lib/connectionStatus';
import UserAvatar from '../components/UserAvatar';

const VIEW_TITLES = {
  home: 'Today',
  tx: 'Ledger',
  budget: 'Plan',
  recurring: 'Plan',
  cashflow: 'Plan',
  goals: 'Plan',
  accounts: 'Accounts',
  settings: 'Settings',
};

// Plan absorbs Budget, Recurring, Cash Flow and Goals: four items on the rail
// instead of seven, with these reachable as tabs inside it. Plan's own design
// hasn't been done yet, so the tab strip is deliberately plain — it keeps the
// pages reachable without inventing a layout for them. Scenarios has no tab:
// it lives under Cash Flow's projected balance, next to the chart it changes.
const PLAN_TABS = [
  { view: 'budget', label: 'Budget' },
  { view: 'recurring', label: 'Recurring' },
  { view: 'cashflow', label: 'Cash Flow' },
  { view: 'goals', label: 'Goals' },
];
const PLAN_VIEWS = new Set(PLAN_TABS.map((t) => t.view));

// Which rail item a view lights. Settings lights none of the four: the account
// button at the foot of the rail carries its highlight, where Today used to.
export const navKeyOf = (v) =>
  v === 'tx' ? 'tx'
    : PLAN_VIEWS.has(v) ? 'plan'
      : v === 'accounts' ? 'accounts'
        : v === 'settings' ? 'settings'
          : 'home';

// "Logan" from "Logan Brewer" — the email, as before, when the account has no name.
export const firstNameOf = (user) =>
  String(user?.displayName || '').trim().split(/\s+/)[0] || user?.email || '';

// VIEW_TITLES names the rail section (the eyebrow); this names the page itself
// (the h1). On Plan the two differ — "Plan / Budget" — which is what lets the
// tab strip change the heading without losing where you are.
const PAGE_TITLES = {
  tx: 'Transactions',
  budget: 'Budget',
  recurring: 'Recurring',
  cashflow: 'Cash Flow',
  goals: 'Goals',
  accounts: 'Accounts',
  settings: 'Settings',
};

// Views whose figures are filtered by `selectedMonth`. 'tx' is excluded because
// it carries its own picker in txTopbar, alongside a range chip the bare picker
// would conflict with. 'home' is excluded because everything on Today is
// anchored to today — a month control there would be a dead control. The rest
// of Plan (recurring, cashflow, goals) and accounts are
// forward-looking or not time-scoped at all.
const MONTH_SCOPED_VIEWS = new Set(['budget']);

export default function FocusShell({
  user,
  profilePhotoUrl,
  theme,
  setTheme,
  selectedMonth,
  onSelectedMonthChange,
  balance,
  runway,
  cards = [],
  accounts = [],
  income,
  expenses,
  displayTx,
  budgetRows,
  // Pinned to the current month by App.jsx. Today has no picker, so it must
  // not inherit one: `budgetRows` follows `selectedMonth` and would show
  // another month's remaining budgets under a hero labelled "today".
  todayBudgetRows,
  recurring,
  forecast,
  budgetProps,
  recurringProps,
  cashFlowProps,
  accountsProps,
  onEditTransaction,
  onDeleteTransaction,
  categories,
  categoryMeta,
  goals,
  transactionsRaw,
  onSaveGoal,
  onDeleteGoal,
  onArchiveGoal,
  scenarios = [],
  onSaveScenario,
  onDeleteScenario,
  onToggleScenario,
  onAddCategory,
  onRemoveCategory,
  onRenameCategory,
  onSetCategoryAppearance,
  settingsProps,
  onSyncAll,
  bankSyncing,
  hasBankSync,
  lastSyncedAt,
  connectionIssues = [],
  onReconnectBank,
  balanceAsOf,
}) {
  const [view, setView] = useState('home');
  const [detail, setDetail] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [txSearch, setTxSearch] = useState('');
  const [txDateRange, setTxDateRange] = useState('all');
  // Set when the user drills into a budget: the transactions screen opens
  // scoped to that category so they can see what the number is made of.
  const [txCategory, setTxCategory] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 900);
  const [isTablet,  setIsTablet]  = useState(() => window.innerWidth >= 640 && window.innerWidth < 900);
  const [connBannerDismissed, setConnBannerDismissed] = useState(false);
  const [showTips, setShowTips] = useState(() => {
    try { return localStorage.getItem('showTips') === 'true'; } catch { return false; }
  });

  const toggleShowTips = (val) => {
    setShowTips(val);
    try { localStorage.setItem('showTips', val); } catch {}
  };

  useEffect(() => {
    const fn = () => {
      const w = window.innerWidth;
      setIsDesktop(w >= 900);
      setIsTablet(w >= 640 && w < 900);
    };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const D = isDesktop;
  const now = new Date();
  const isCurrentMonth = selectedMonth.getMonth() === now.getMonth() && selectedMonth.getFullYear() === now.getFullYear();
  const monthLabel = isCurrentMonth
    ? 'This month'
    : selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const pageTitle = VIEW_TITLES[view];

  const accountNameMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a.name])),
    [accounts],
  );

  const navTo = (v) => {
    setView(v);
    if (v !== 'tx') setTxSearch('');
    setTxCategory(null);
  };

  // Drill into a budget. The month range comes along because a budget IS a
  // monthly figure — landing on all-time would show a total that doesn't match
  // the number the user just clicked.
  const navToCategory = (cat) => {
    navTo('tx');
    setTxCategory(cat);
    setTxDateRange('month');
  };

  const sideNavRef = useRef(null);
  const mobileNavRef = useRef(null);

  // Four items, and Plan stands for any of the views it absorbed — so arrow
  // keys land on the rail item that is actually lit.
  const navItemsForKeyboard = [
    { key: 'home', view: 'home' },
    { key: 'tx', view: 'tx' },
    { key: 'plan', view: 'budget' },
    { key: 'accounts', view: 'accounts' },
  ];

  const handleSideNavKeyDown = useCallback((e) => {
    const items = navItemsForKeyboard;
    const currentIdx = items.findIndex((n) => n.key === navKeyOf(view));
    let nextIdx = currentIdx;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      nextIdx = (currentIdx + 1) % items.length;
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      nextIdx = (currentIdx - 1 + items.length) % items.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIdx = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIdx = items.length - 1;
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Select whichever tab currently has DOM focus
      if (sideNavRef.current) {
        const focused = sideNavRef.current.querySelector('[role="tab"]:focus');
        if (focused) {
          const focusedIdx = Array.from(sideNavRef.current.querySelectorAll('[role="tab"]')).indexOf(focused);
          if (focusedIdx >= 0) navTo(items[focusedIdx].view);
        }
      }
      return;
    } else {
      return;
    }

    // Move DOM focus to the next tab button
    if (sideNavRef.current && nextIdx !== currentIdx) {
      const tabs = sideNavRef.current.querySelectorAll('[role="tab"]');
      if (tabs[nextIdx]) tabs[nextIdx].focus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleMobileNavKeyDown = useCallback((e) => {
    const items = navItemsForKeyboard;
    const currentIdx = items.findIndex((n) => n.key === navKeyOf(view));
    let nextIdx = currentIdx;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextIdx = (currentIdx + 1) % items.length;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nextIdx = (currentIdx - 1 + items.length) % items.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIdx = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIdx = items.length - 1;
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (mobileNavRef.current) {
        const focused = mobileNavRef.current.querySelector('[role="tab"]:focus');
        if (focused) {
          const focusedIdx = Array.from(mobileNavRef.current.querySelectorAll('[role="tab"]')).indexOf(focused);
          if (focusedIdx >= 0) navTo(items[focusedIdx].view);
        }
      }
      return;
    } else {
      return;
    }

    if (mobileNavRef.current && nextIdx !== currentIdx) {
      const tabs = mobileNavRef.current.querySelectorAll('[role="tab"]');
      if (tabs[nextIdx]) tabs[nextIdx].focus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Show the balance's age once it's old enough to be misleading (>48h) —
  // usually a sign the bank connection has gone stale.
  const BALANCE_AGE_MS = 48 * 60 * 60 * 1000;
  const balanceAgeLabel = balanceAsOf && (Date.now() - balanceAsOf.getTime() > BALANCE_AGE_MS)
    ? `as of ${balanceAsOf.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : null;

  // Dashboard banner when a bank connection is broken (needs re-auth) or has
  // quietly stopped syncing. A stale-only banner is dismissible for the
  // session; a DISCONNECTED one is not — nothing syncs again until the user
  // reconnects, so it stays up until the reconnect succeeds.
  const connectionBanner = (() => {
    if (connectionIssues.length === 0) return null;
    const issue = connectionIssues.find((e) => e.disconnected) || connectionIssues[0];
    if (connBannerDismissed && !issue.disconnected) return null;
    const name = issue.institution || issue.accountLabel || 'A bank';
    const since = issue.lastSynced
      ? new Date(issue.lastSynced).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : null;
    const reason = issue.disconnected ? disconnectReasonText(issue.disconnectReason) : null;
    const message = issue.disconnected
      ? `${name} needs to be reconnected${reason ? ` — ${reason}` : ''}${since ? `. Transactions stopped syncing ${since}` : ''}. Recent activity may be missing.`
      : `${name} hasn't synced since ${since}. Recent activity may be missing.`;
    return (
      <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 14, marginBottom: 16, background: 'color-mix(in srgb,var(--warn) 12%,transparent)', border: '1px solid color-mix(in srgb,var(--warn) 35%,transparent)' }}>
        <Icon name="alert" size={18} style={{ color: 'var(--warn)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, minWidth: 0 }}>{message}</span>
        {issue.disconnected && onReconnectBank ? (
          <button type="button" onClick={() => onReconnectBank(issue)} className="pressable" style={{ flexShrink: 0, height: 32, padding: '0 14px', borderRadius: 9, background: 'var(--warn)', color: 'var(--bg)', fontSize: 12.5, fontWeight: 600 }}>
            Reconnect
          </button>
        ) : (
          <button type="button" onClick={onSyncAll} disabled={bankSyncing} className="pressable chip" style={{ flexShrink: 0, fontSize: 12.5 }}>
            {bankSyncing ? 'Syncing…' : 'Sync now'}
          </button>
        )}
        {!issue.disconnected && (
          <button type="button" onClick={() => setConnBannerDismissed(true)} aria-label="Dismiss" className="pressable iconwrap" style={{ width: 28, height: 28, borderRadius: 8, color: 'var(--ink-3)', flexShrink: 0, fontSize: 14, fontWeight: 600 }}>
            ✕
          </button>
        )}
      </div>
    );
  })();

  // Shown in the hero's place while there is nothing to forecast from, and
  // alongside it while the user is still setting up (Settings › show tips).
  // An element, not a component: declared as a component inside this one it
  // changed identity every render and remounted, replaying its entrance
  // animation each time anything on the shell changed.
  const welcomeCard = (displayTx.length > 0 && !showTips) ? null : (
      <div className="rise" style={{ maxWidth: 420 }}>
        <div className="today-eyebrow">Welcome to Prisma</div>
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, margin: '10px 0 18px' }}>
          Connect a bank and Prisma works out what's safe to spend — your cash today, minus
          every bill due before your next deposit.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { icon: 'bank', label: 'Connect a bank account', target: 'accounts' },
            { icon: 'plan', label: 'Set your budgets', target: 'budget' },
            { icon: 'list', label: 'Browse transactions', target: 'tx' },
          ].map(({ icon, label, target }) => (
            <button key={label} onClick={() => navTo(target)} className="pressable" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--line)', color: 'var(--ink-2)', fontSize: 13.5, fontWeight: 500, textAlign: 'left' }}>
              <Icon name={icon} size={18} style={{ color: 'var(--accent)', flexShrink: 0 }} />
              {label}
            </button>
          ))}
        </div>
      </div>
  );

  // A row on "out before payday" is one occurrence of a recurring series, so
  // the detail sheet opens on the series it came from. Falling back to the
  // ledger event keeps card payments — which are projected, not detected —
  // openable too.
  const openSeriesFromRow = (row) => {
    // A planned series has no transaction to show; it's edited on Recurring.
    if (row.event?.planned) {
      navTo('recurring');
      return;
    }
    const key = row.event?.seriesKey;
    const series = key ? (recurring || []).find((r) => r.key === key) : null;
    if (series) {
      setDetail({ ...series, date: row.date, raw: series });
      return;
    }
    setDetail({
      desc: row.label,
      amt: row.amount,
      type: 'expense',
      date: row.date,
      cat: row.event?.category || null,
    });
  };

  // Also an element rather than a component, for the same reason: `<Home />`
  // remounted the whole of Today — chart included — on every shell render.
  const home = (
    <TodayView
      balance={balance}
      runway={runway}
      cards={cards}
      budgetRows={todayBudgetRows || budgetRows}
      balanceAgeLabel={balanceAgeLabel}
      onOpenSeries={openSeriesFromRow}
      onOpenCategory={navToCategory}
      welcome={welcomeCard}
      banner={connectionBanner}
      compact={!D && !isTablet}
    />
  );


// Module scope, not a closure inside FocusShell. Declared inside the parent it
// was a *new component type* on every render, so React threw the subtree away
// and rebuilt it each time any shell state changed — the type filter and the
// account select silently reset on a resize, and every `rise` animation
// replayed. Everything it used to close over is a prop now.
function TransactionsPage({
  search = '',
  displayTx,
  accounts,
  accountNameMap,
  txCategory,
  onClearCategory,
  txDateRange,
  setTxDateRange,
  selectedMonth,
  isCurrentMonth,
  monthLabel,
  desktop: D,
  onPick,
}) {
  const [f, setF] = useState('all');
  const [acct, setAcct] = useState('all');
  const dateRange = txDateRange;
  const setDateRange = setTxDateRange;

  let rangeStart = null;
  if (dateRange === 'month') rangeStart = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
  else if (dateRange === '3mo') { rangeStart = new Date(selectedMonth); rangeStart.setMonth(rangeStart.getMonth() - 3); }
  else if (dateRange === 'year') rangeStart = new Date(selectedMonth.getFullYear(), 0, 1);

  const items = displayTx
    .filter((t) => f === 'all' || t.type === f)
    .filter((t) => !txCategory || (t.cat || t.raw?.category) === txCategory)
    .filter((t) => acct === 'all' || t.raw?.accountId === acct)
    .filter((t) => !rangeStart || new Date(t.date) >= rangeStart)
    .filter((t) => !search || t.desc.toLowerCase().includes(search.toLowerCase()) || t.raw?.description?.toLowerCase().includes(search.toLowerCase()));

  const moneyIn = items.filter((t) => t.type === 'income').reduce((s, t) => s + t.amt, 0);
  // Refunds come off what went out rather than adding to what came in.
  const moneyOut = items.reduce((s, t) => s + spendDelta(t), 0);
  const periodLabel = dateRange === 'all' ? 'all time'
    : dateRange === 'month' ? (isCurrentMonth ? 'this month' : monthLabel)
    : dateRange === '3mo' ? 'last 3 months'
    : 'this year';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: D ? 860 : 'none', margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{items.length} transaction{items.length !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 18 }}>
          {[
            { label: `Money in · ${periodLabel}`, val: moneyIn, color: 'var(--pos)', sign: false },
            { label: `Money out · ${periodLabel}`, val: moneyOut, color: 'var(--ink)', sign: false },
            { label: `Net · ${periodLabel}`, val: moneyIn - moneyOut, color: 'var(--accent)', sign: true },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>{s.label}</div>
              <div className="num" style={{ fontSize: 17, fontWeight: 600, color: s.color }}>{money(s.val, { sign: s.sign, cents: false })}</div>
            </div>
          ))}
        </div>
      </div>

      {txCategory && (
        // The scope came from somewhere else on the app, so it has to be
        // visible and removable here — otherwise the list silently hides rows.
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Showing</span>
          <button
            type="button"
            onClick={onClearCategory}
            className="pressable chip is-active"
            aria-label={`Clear ${txCategory} filter`}
            style={{ fontSize: 12, height: 28, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <CatBadge cat={txCategory} size={18} radius={6} />
            {txCategory}
            <Icon name="plus" size={13} style={{ transform: 'rotate(45deg)' }} />
          </button>
        </div>
      )}

      {/* One filter bar: type and range read as two groups split by a hairline,
          with the account select pushed to the trailing edge. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ChipFilter
          size="sm"
          options={[
            { value: 'all', label: 'All' },
            { value: 'income', label: 'Income' },
            { value: 'expense', label: 'Expenses' },
          ]}
          value={f}
          onChange={setF}
        />

        <div aria-hidden style={{ width: 1, height: 18, background: 'var(--line)', flex: 'none' }} />

        <ChipFilter
          size="sm"
          options={[
            { value: 'all', label: 'All time' },
            { value: 'month', label: isCurrentMonth ? 'This month' : monthLabel },
            { value: '3mo', label: '3 months' },
            { value: 'year', label: 'This year' },
          ]}
          value={dateRange}
          onChange={setDateRange}
        />

        {accounts.length > 1 && (
          // Capped: a native select otherwise sizes itself to its longest
          // account name and swallows the rest of the row.
          <select value={acct} onChange={(e) => setAcct(e.target.value)} aria-label="Filter by account" style={{ marginLeft: 'auto', height: 30, maxWidth: 180, padding: '0 10px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 99, fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', outline: 'none', cursor: 'pointer', textOverflow: 'ellipsis' }}>
            <option value="all">All accounts</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>

      {/* The tabular form has fixed columns (~450px before the amount), so it
          is desktop-only; narrow viewports get the stacked row, which carries
          the same facts in two lines. */}
      {items.length ? (
        <TxList items={items} onPick={onPick} accountNames={accountNameMap} table={D} />
      ) : (
        <p className="pg-note" style={{ paddingTop: 24 }}>No transactions match those filters.</p>
      )}
    </div>
  );
}

  const planTabs = (
    <div role="tablist" aria-label="Plan sections" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {PLAN_TABS.map((t) => {
        const active = view === t.view;
        return (
          <button
            key={t.view}
            role="tab"
            aria-selected={active}
            onClick={() => navTo(t.view)}
            className={`chip pressable ${active ? 'is-active' : ''}`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );

  // Every non-Today page opens the same way: eyebrow, title, and the one
  // control the page actually answers to. The eyebrow names the rail section,
  // so a Plan tab still says where you are once the title changes.
  const pageHead = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="eyebrow">{pageTitle}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <h1 className="display" style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--ink)' }}>
          {PAGE_TITLES[view] || pageTitle}
        </h1>
        {MONTH_SCOPED_VIEWS.has(view) && (
          <MonthPicker
            value={selectedMonth}
            onChange={onSelectedMonthChange}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8, background: 'transparent', border: '1px solid var(--line)', fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}
          />
        )}
      </div>
    </div>
  );

  const screen = {
    home,
    tx: (
      <TransactionsPage
        search={txSearch}
        displayTx={displayTx}
        accounts={accounts}
        accountNameMap={accountNameMap}
        txCategory={txCategory}
        onClearCategory={() => setTxCategory(null)}
        txDateRange={txDateRange}
        setTxDateRange={setTxDateRange}
        selectedMonth={selectedMonth}
        isCurrentMonth={isCurrentMonth}
        monthLabel={monthLabel}
        desktop={D}
        onPick={setDetail}
      />
    ),
    budget: <BudgetView desktop={D} {...budgetProps} monthLabel={monthLabel} onViewTransactions={navToCategory} />,
    recurring: <RecurringView desktop={D} {...recurringProps} onGoToTransactions={() => navTo('tx')} />,
    cashflow: (
      <CashFlowView
        {...cashFlowProps}
        scenarios={scenarios}
        onSaveScenario={onSaveScenario}
        onDeleteScenario={onDeleteScenario}
        onToggleScenario={onToggleScenario}
        onGoToRecurring={() => navTo('recurring')}
      />
    ),
    accounts: <AccountsView desktop={D} {...accountsProps} />,
    goals: <GoalsView goals={goals || []} transactions={transactionsRaw || []} categories={categories} onSave={onSaveGoal} onDelete={onDeleteGoal} onArchive={onArchiveGoal} desktop={D} />,
    settings: (
      <FocusSettings
        theme={theme}
        setTheme={setTheme}
        onCats={() => setCatOpen(true)}
        onBudgets={() => navTo('budget')}
        isDesktop={D}
        user={user}
        showTips={showTips}
        onToggleShowTips={toggleShowTips}
        {...settingsProps}
      />
    ),
  }[view];

  // No icons in the rail. At four items the labels are unambiguous, and
  // dropping the glyphs is most of what makes it feel quiet.
  const navItems = [
    { key: 'home', label: 'Today', view: 'home' },
    { key: 'tx', label: 'Ledger', view: 'tx' },
    { key: 'plan', label: 'Plan', view: 'budget' },
    { key: 'accounts', label: 'Accounts', view: 'accounts' },
  ];

  // Mobile keeps its icons: a bottom tab bar with none reads as unlabelled
  // chrome, and the rail's quiet is a desktop-density argument.
  const mobileNavIcons = { home: 'home2', tx: 'list', plan: 'plan', accounts: 'bank' };

  const activeNavKey = navKeyOf(view);
  // On Settings no tab is selected, so Today keeps the tab stop.
  const railHasActive = navItems.some((n) => n.key === activeNavKey);

  const userAvatar = (size, fontSize) => (
    <UserAvatar user={user} profilePhotoUrl={profilePhotoUrl} size={size} fontSize={fontSize} />
  );

  const overlays = (
    <>
      <TxDetail
        tx={detail}
        accountName={(() => {
          const id = detail?.raw?.accountId || detail?.accountId;
          return accounts.find((a) => a.id === id)?.name || detail?.raw?.accountName || '';
        })()}
        onClose={() => setDetail(null)}
        onEdit={(tx) => { setDetail(null); onEditTransaction(tx); }}
        onDelete={(tx) => { setDetail(null); onDeleteTransaction(tx); }}
      />
      <CategoryManager open={catOpen} onClose={() => setCatOpen(false)} categories={categories} categoryMeta={categoryMeta} onAdd={onAddCategory} onRemove={onRemoveCategory} onRename={onRenameCategory} onSetAppearance={onSetCategoryAppearance} />
    </>
  );

  const txSearchInput = (
    <div className={isTablet ? 'tablet-full-width' : ''} style={{ flex: 1, minWidth: 0, maxWidth: D ? 360 : undefined, position: 'relative' }}>
      <Icon name="search" size={D ? 15 : 18} style={{ position: 'absolute', left: D ? 12 : 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', pointerEvents: 'none' }} />
      <input
        value={txSearch}
        onChange={(e) => setTxSearch(e.target.value)}
        placeholder="Search transactions…"
        style={{
          width: '100%',
          height: D ? 36 : 44,
          paddingLeft: D ? 36 : 42,
          paddingRight: 12,
          background: D ? 'var(--surface)' : 'var(--surface-2)',
          border: `1px solid ${txSearch ? 'var(--accent)' : 'var(--line)'}`,
          borderRadius: D ? 10 : 13,
          fontSize: D ? 13.5 : 15,
          color: 'var(--ink)',
          outline: 'none',
          boxShadow: !D && txSearch ? '0 0 0 3px color-mix(in srgb, var(--accent) 20%, transparent)' : 'none',
        }}
      />
    </div>
  );

  const txTopbar = (
    <header style={{
      height: D ? 64 : undefined,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: D ? '0 28px' : '12px 18px 8px',
      background: D ? 'color-mix(in srgb, var(--bg) 85%, transparent)' : 'var(--bg)',
      backdropFilter: D ? 'blur(8px)' : undefined,
      borderBottom: D ? '1px solid var(--line)' : undefined,
    }}>
      <div
        title={txDateRange === 'all' ? 'Switch off "All time" below to use a specific month' : 'Sets the reference month for the range chip below'}
        style={{ opacity: txDateRange === 'all' ? 0.45 : 1, pointerEvents: txDateRange === 'all' ? 'none' : 'auto', flexShrink: 0 }}
      >
        <MonthPicker
          value={selectedMonth}
          onChange={onSelectedMonthChange}
          style={{ display: 'flex', alignItems: 'center', gap: 6, height: D ? 36 : 44, padding: D ? '0 14px' : '0 12px', borderRadius: D ? 10 : 13, background: 'var(--surface)', border: '1px solid var(--line)', fontSize: D ? 13.5 : 13, fontWeight: 600, color: 'var(--ink)', flexShrink: 0 }}
        />
      </div>
      {txSearchInput}
    </header>
  );

  if (D) {
    return (
      <div className="app-shell" style={{ display: 'flex', height: '100vh', background: 'var(--bg)' }}>
        <aside style={{ width: 196, flexShrink: 0, borderRight: '1px solid var(--line)', background: 'var(--bg)', padding: '22px 14px', display: 'flex', flexDirection: 'column', gap: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 6px' }}>
            <AppLogo size={22} />
            <span style={{ fontWeight: 600, fontSize: 16, color: 'var(--ink)', letterSpacing: '-0.02em' }}>Prisma</span>
          </div>

          <div ref={sideNavRef} role="tablist" aria-label="Main navigation" aria-orientation="vertical" onKeyDown={handleSideNavKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {navItems.map((n) => {
              const active = activeNavKey === n.key;
              return (
                <button
                  key={n.key}
                  role="tab"
                  aria-selected={active}
                  tabIndex={active || (!railHasActive && n.key === 'home') ? 0 : -1}
                  onClick={() => navTo(n.view)}
                  className={`today-nav-item ${active ? 'is-active' : ''}`}
                >
                  {n.label}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Syncing is push-driven (Plaid webhooks write straight to the
                database), so this is reassurance first and a control second —
                quiet freshness text you can click on the rare webhook miss. */}
            {hasBankSync && lastSyncedAt && (
              <button type="button" onClick={onSyncAll} disabled={bankSyncing} className="pressable" title="Sync now" style={{ fontSize: 11, color: 'var(--ink-3)', background: 'transparent', textAlign: 'left', padding: '0 6px' }}>
                {bankSyncing
                  ? 'Updating…'
                  : `Synced ${relTime(lastSyncedAt)}${accounts.length ? ` · ${accounts.length} account${accounts.length === 1 ? '' : 's'}` : ''}`}
              </button>
            )}
            {/* A rail item in all but position: same pill, same inverted
                highlight while Settings is open. */}
            <button
              type="button"
              onClick={() => navTo('settings')}
              aria-label="Open settings"
              aria-current={view === 'settings' ? 'page' : undefined}
              className={`today-nav-item pressable ${view === 'settings' ? 'is-active' : ''}`}
              style={{ gap: 9, height: 40, padding: '0 6px', minWidth: 0 }}
            >
              {userAvatar(26, 11)}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{firstNameOf(user)}</span>
            </button>
          </div>
        </aside>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {view === 'tx' && txTopbar}
          {/* Today owns its own padding and scroll — it is a full-bleed page,
              not a card grid inside the standard wrapper, and it carries no
              page title: the hero is the title. */}
          {view === 'home' ? (
            <main className="scroll" style={{ flex: 1, minHeight: 0, display: 'flex', background: 'var(--bg)' }}>
              {screen}
            </main>
          ) : (
            <main className="scroll" style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)' }}>
              <div className="pg" style={{ maxWidth: 1180, padding: '34px 44px 44px', width: '100%' }}>
                {pageHead}
                {PLAN_VIEWS.has(view) && planTabs}
                {screen}
              </div>
            </main>
          )}
        </div>
        {overlays}
      </div>
    );
  }

  return (
    <div className="app-shell" style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
      {view === 'tx' ? (
        txTopbar
      ) : view === 'home' ? (
        // The hero is the title, so the bar carries only the mark and the way
        // into settings.
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 0', flexShrink: 0, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <AppLogo size={22} />
            <span style={{ fontWeight: 600, fontSize: 16, color: 'var(--ink)', letterSpacing: '-0.02em' }}>Prisma</span>
          </div>
          <button onClick={() => navTo('settings')} aria-label="Open settings" className="pressable" style={{ width: 34, height: 34, borderRadius: 999, padding: 0, overflow: 'hidden', border: '1px solid var(--line)', background: 'transparent' }}>
            {userAvatar(34, 13)}
          </button>
        </header>
      ) : (
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 10px', flexShrink: 0, gap: 12 }}>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="eyebrow" style={{ fontSize: 11 }}>{pageTitle}</div>
            <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.03em' }}>{PAGE_TITLES[view] || pageTitle}</h1>
            {MONTH_SCOPED_VIEWS.has(view) && (
              <div style={{ marginTop: 4 }}>
                <MonthPicker
                  value={selectedMonth}
                  onChange={onSelectedMonthChange}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--line)', fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}
                />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navTo('settings')} aria-label="Open settings" className="pressable" style={{ width: 40, height: 40, borderRadius: 999, padding: 0, overflow: 'hidden', border: '1px solid var(--line)', background: 'transparent' }}>
              {userAvatar(40, 15)}
            </button>
          </div>
        </header>
      )}

      {view === 'home' ? (
        <div className="scroll" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {screen}
        </div>
      ) : (
        <div className="scroll pg" style={{ flex: 1, overflowY: 'auto', padding: '10px 16px 90px' }}>
          {PLAN_VIEWS.has(view) && planTabs}
          {screen}
        </div>
      )}

      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 12px 16px', pointerEvents: 'none' }}>
        <nav ref={mobileNavRef} role="tablist" aria-label="Main navigation" onKeyDown={handleMobileNavKeyDown} style={{ display: 'flex', alignItems: 'center', height: 62, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 22, boxShadow: 'var(--shadow-md)', pointerEvents: 'auto', padding: '0 4px' }}>
          {navItems.map((n) => {
            const active = activeNavKey === n.key;
            return (
              <button key={n.key} role="tab" aria-selected={active} tabIndex={active || (!railHasActive && n.key === 'home') ? 0 : -1} onClick={() => navTo(n.view)} aria-label={n.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: active ? 'var(--ink)' : 'var(--ink-3)' }}>
                <Icon name={mobileNavIcons[n.key]} size={22} sw={active ? 2.2 : 1.8} />
                <span style={{ fontSize: 10.5, fontWeight: 600 }}>{n.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
      {overlays}
    </div>
  );
}
